import type {
  DepositStatus as DepositStatusValue,
  DepositMovementKind,
  DepositEntriesSyncStatus,
  DepositRow,
} from '@/lib/services/deposits/deposits.types'
import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import { CircleCheck } from 'lucide-react'
import { DEPOSIT_STATUS_LABELS } from './-presentation'

const WITHDRAWAL_STATUS_LABELS: Record<DepositStatusValue, string> = {
  paid: 'Collected',
  pending: 'Withdrawal pending',
  in_transit: 'Processing',
  failed: 'Withdrawal failed',
  canceled: 'Withdrawal canceled',
}

export function DepositStatus({
  status,
  movementKind = 'deposit',
}: {
  status?: DepositStatusValue | null
  movementKind?: DepositMovementKind
}) {
  const isWithdrawal = movementKind === 'bank_withdrawal'
  const label = isWithdrawal
    ? ((status && WITHDRAWAL_STATUS_LABELS[status]) ?? status ?? 'Unknown')
    : ((status && DEPOSIT_STATUS_LABELS[status]) ?? status ?? 'Unknown')

  if (status === 'paid') {
    return (
      <span className="inline-flex items-center gap-1.5 text-status-success-foreground">
        <CircleCheck className="size-4" aria-hidden="true" />
        <Typography variant="label" tone="inherit">
          {label}
        </Typography>
      </span>
    )
  }

  const isException = status === 'failed' || status === 'canceled'

  return (
    <Badge variant={isException ? 'destructive' : 'outline'} size="status" indicator={isException}>
      {label}
    </Badge>
  )
}

export function ReconciliationStatus({
  status,
  differenceMinor = 0,
}: {
  status?: DepositEntriesSyncStatus | null
  differenceMinor?: DepositRow['reconciliation_difference_minor']
}) {
  if (status === 'complete' && Number(differenceMinor) === 0) {
    return null
  }

  const isFailure = status === 'failed' || status === 'mismatch'

  return (
    <Badge variant={isFailure ? 'destructive' : 'warning'} size="status" indicator>
      {status === 'mismatch'
        ? 'Amounts do not match'
        : status === 'unavailable'
          ? 'Breakdown unavailable'
          : status === 'pending'
            ? 'Breakdown pending'
            : 'Needs review'}
    </Badge>
  )
}
